import { useState, useRef, useEffect } from 'react';
import { FaqItem, Pagination, Faqable, Translation } from 'src/types';
import { FiSearch, FiX } from 'react-icons/fi';
import UserFaqItem from 'src/components/UserFaqItem';
import { Modal } from 'react-bootstrap';
import { Formik, Form, Field } from 'formik';
import ReCAPTCHA from 'react-google-recaptcha';
import { RECAPTCHA_KEY } from 'src/lib/constants';
import * as yup from 'yup';
import ApiClient from 'src/lib/api_client';
import { debounce, omit, sortBy } from 'lodash-es';
import classNames from 'classnames';
import useEnhanceApiClient from 'src/hooks/useEnhanceApiClient';
import { useTranslations } from 'next-intl';
import ErrorMesseageTranslation from 'src/components/ErrorMesseageTranslation';

const CreateFaqValidationSchema = yup.object().shape({
  username: yup
    .string()
    .required('validationNameRequired')
    .typeError('validationNameTypeError'),
  email: yup
    .string()
    .email('validationEmailFormatError')
    .required('validationEmailRequired')
    .typeError('validationEmailTypeError'),
  question: yup
    .string()
    .required('validationQuestionRequired')
    .typeError('validationQuestionTypeError'),
  [RECAPTCHA_KEY]: yup
    .string()
    .required('validationCaptchaRequired')
    .typeError('validationCaptchaTypeError'),
});

const UpdateFaqValidationSchema = yup.object().shape({
  answer: yup
    .string()
    .required('validationAnwserRequired')
    .typeError('validationAnwserTypeError'),
});

export const groupFaqsItems = (faqs: FaqItem[]) => {
  const groupedFaqs = faqs.reduce<Record<string, FaqGroup>>((memo, faq) => {
    const { groupId, groupTitle, id, position } = faq;
    const currentPos = Number(position ?? id)
    const key = groupId || '';

    if (!memo[key]) {
      memo[key] = { groupId: key, groupTitle, faqs: [], position: currentPos };
    }

    const group = memo[key];
    group.faqs.push(omit(faq, ['groupTitle', 'groupId']));
    group.position = Math.min(group.position, currentPos);
    return memo;
  }, {});

  return sortBy(Object.values(groupedFaqs), 'position')
};

type FaqGroup = {
  groupId: string;
  groupTitle?: string;
  faqs: FaqItem[];
  position: number
};

type Props = {
  faqs: FaqItem[];
  faqable: Faqable;
  faqPagination: Pagination;
  isAdmin: boolean;
  hiddenAskQuestion?: boolean;
  showViewAllButtonOnly?: boolean;
  onCloseModal?: ()=> void;
  outerFaq?: FaqItem;
  title?: string;
  limit?: number;
};

const UserFaqSection: React.FC<Props> = ({
  faqs: initialFaqs,
  faqPagination,
  faqable,
  isAdmin,
  hiddenAskQuestion,
  showViewAllButtonOnly,
  onCloseModal,
  outerFaq,
  title,
  limit,
}) => {
  const translation = useTranslations('components.UserFaqSection')
  const filterFaq = useEnhanceApiClient(ApiClient.filterFaq);
  const createFaq = useEnhanceApiClient(ApiClient.createFaq);
  const updateFaq = useEnhanceApiClient(ApiClient.updateFaq);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [faqs, setFaqs] = useState(initialFaqs);
  const [selectedFaq, setSelectedFaq] = useState<FaqItem>();
  const [showCreateQuestionModal, setShowCreateQuestionModal] = useState(false);
  const [showUpdateQuestionModal, setShowUpdateQuestionModal] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [query, setQuery] = useState<string | null>(null);
  const [showAllFaqs, setShowAllFaqs] = useState(false);
  const limitValue = limit || initialFaqs.length;
  const groupedFaqs = groupFaqsItems(faqs)

  const filter = (text: string | null, page: number = 1) => {
    filterFaq({
      text: text,
      page: page,
      faqable_id: faqable.id,
      faqable_type: faqable.type,
      per_page: 100,
    }).then((result) => {
      const [json] = result;
      if (page > 1) {
        setFaqs((prev) => [...prev, ...json.data]);
      } else {
        setFaqs(json.data);
      }
    });
  };

  const searchFaqs = debounce(async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.target instanceof HTMLInputElement) {
      setQuery(e.target.value);
      filter(e.target.value);
    }
  }, 200);

  const fetchFaqs = ()=> {
    if (faqPagination.nextPage) {
      filter(null);
    }
    setTimeout(() => {
      setShowAllFaqs(true);
    }, 200);
  }

  useEffect(()=> {
    if (outerFaq) {
      fetchFaqs()
    }
  },[outerFaq?.id])

  return (
    <>
      {showViewAllButtonOnly ? (
        <div
          className="btn tw-bg-brand-green tw-text-brand-navy tw-rounded-4xl tw-py-3 tw-px-8 tw-normal-case"
          onClick={() => fetchFaqs()}
        >
          {translation('viewAllQuestion')}
        </div>
      ) : null}
      <div className={classNames({"tw-hidden": showViewAllButtonOnly})}>
        <h2
          className={classNames(
            'tw-text-center tw-font-styleSans tw-font-semibold tw-text-3xl md:tw-text-4xl tw-leading-none md:tw-leading-none md:tw-max-w-full tw-max-w-xs tw-mx-auto',
            {
              'tw-mb-6 md:tw-mb-8': faqs.length > 0 || query,
              'tw-mb-0': faqs.length === 0 && !query,
            }
          )}
        >
          {faqs.length === 0 && !query ? translation('haveAQuestion') : title || translation('helpfulQuestions')}
        </h2>

        <div>
          {faqs.slice(0, limitValue ? limitValue : undefined).map((faq) => (
            <UserFaqItem
              key={faq.id}
              faq={faq}
              onClickEdit={(faq) => {
                setSelectedFaq(faq);
                setShowUpdateQuestionModal(true);
              }}
              onDelete={(faq) => {
                setFaqs((prev) => prev.filter((o) => o.id !== faq.id));
              }}
              faqable={faqable}
              isAdmin={isAdmin}
            />
          ))}
        </div>

        {faqPagination.nextPage || (limitValue && limitValue < faqs.length) ? (
          <div className="tw-mt-8 tw-text-center">
            <button
              type="button"
              className={classNames('btn tw-normal-case', {
                'tw-text-white tw-bg-orange': faqable.type === 'SupplierActivityOption',
                'tw-text-brand-navy tw-border tw-border-brand-navy tw-border-solid':
                  faqable.type !== 'SupplierActivityOption',
              })}
              onClick={() => {
                fetchFaqs()
              }}
            >
              {translation('showAllFaqs')}
            </button>
          </div>
        ) : null}

        {!hiddenAskQuestion ? (
          <div className="tw-mt-4 tw-text-center">
            <div
              className={classNames('tw-mb-4', {
                'tw-hidden': faqs.length > 0,
              })}
            >
              {translation('yourQuestionWould')}
            </div>
            <div
              className="tw-font-styleSans tw-underline tw-font-semibold tw-cursor-pointer"
              onClick={() => {
                setShowCreateQuestionModal(true);
              }}
            >
              {translation('askAQuestion')}
            </div>
          </div>
        ) : null}
      </div>

      <Modal show={showCreateQuestionModal} onHide={() => setShowCreateQuestionModal(false)}>
        <Modal.Header>
          <div className="modal-title tw-font-semibold tw-font-styleSans md:tw-text-2xl tw-text-lg">
            {translation('askUsAnything')}
          </div>
          <button type="button" className="close" onClick={() => setShowCreateQuestionModal(false)}>
            <FiX size={24} strokeWidth={2} className="tw-text-black tw-w-6 tw-h-6" />
          </button>
        </Modal.Header>
        <Modal.Body>
          <Formik
            validationSchema={CreateFaqValidationSchema}
            initialValues={{
              username: '',
              email: '',
              question: '',
              kind: 'user',
              faqable_id: faqable.id,
              faqable_type: faqable.type,
              [RECAPTCHA_KEY]: '',
            }}
            onSubmit={(values, { setSubmitting }) => {
              createFaq(values)
                .then((result) => {
                  const [json] = result;
                  if ('error' in json) {
                    return;
                  }

                  setFaqs((prev) => [json, ...prev]);
                  setShowCreateQuestionModal(false);
                })
                .finally(() => {
                  setSubmitting(false);
                });
            }}
          >
            {(formik) => (
              <Form>
                <div className="form-group">
                  <label>{translation('name')}</label>
                  <Field name="username" className="form-control" />
                  <ErrorMesseageTranslation name="username" />
                </div>

                <div className="form-group">
                  <label>{translation('email')}</label>
                  <Field name="email" className="form-control" />
                  <ErrorMesseageTranslation name="email" />
                </div>

                <div className="form-group">
                  <label>{translation('askAQuestion')}</label>
                  <Field name="question" className="form-control" />
                  <ErrorMesseageTranslation name="question" />
                </div>

                <div className="form-group">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY as string}
                    onChange={() => {
                      if (recaptchaRef.current) {
                        const recaptchaValue = recaptchaRef.current.getValue();
                        formik.setFieldValue(RECAPTCHA_KEY, recaptchaValue);
                      }
                    }}
                  />
                  <ErrorMesseageTranslation name={RECAPTCHA_KEY} />
                </div>

                <div className="form-group tw-text-center">
                  <button type="submit" className="btn btn-primary" disabled={formik.isSubmitting}>
                    {translation('send')}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>

      {isAdmin && selectedFaq && (
        <Modal show={showUpdateQuestionModal}>
          <Modal.Header>
            <div className="modal-title tw-font-semibold">{translation('replyToQuestion')}</div>
            <button
              type="button"
              className="close"
              onClick={() => setShowUpdateQuestionModal(false)}
            >
              <FiX size={24} strokeWidth={1} className="tw-text-black" />
            </button>
          </Modal.Header>
          <Modal.Body>
            <Formik
              validationSchema={UpdateFaqValidationSchema}
              initialValues={{
                faqableId: faqable.id,
                faqableType: faqable.type,
                question: selectedFaq.question,
                answer: selectedFaq.answer,
                sendEmailToUser: false,
              }}
              onSubmit={(values, { setSubmitting }) => {
                updateFaq({ ...values, id: selectedFaq.id })
                  .then((result) => {
                    const [json] = result;
                    if ('error' in json) {
                      return;
                    }
                    setFaqs((prev) => {
                      return faqs.map((faq) => {
                        if (faq.id === json.id) {
                          return json;
                        } else {
                          return faq;
                        }
                      });
                    });
                    setShowUpdateQuestionModal(false);
                  })
                  .finally(() => setSubmitting(false));
              }}
            >
              {(formik) => (
                <Form>
                  <div className="form-group">
                    <label>{translation('question')}</label>
                    <Field
                      as="textarea"
                      className="form-control"
                      name="question"
                      readOnly={!selectedFaq.answer}
                    />
                    <ErrorMesseageTranslation name="question" />
                  </div>

                  <div className="form-group">
                    <label>{translation('yourReply')}</label>
                    <Field as="textarea" className="form-control" name="answer" />
                    <ErrorMesseageTranslation name="answer" />
                  </div>

                  {!selectedFaq.answer && (
                    <div className="form-group">
                      <div className="form-check">
                        <Field
                          type="checkbox"
                          name="sendEmailToUser"
                          className="form-check-input"
                          id="send-email-to-user"
                        />
                        <label className="form-check-label" htmlFor="send-email-to-user">
                          {translation('sendEmailToInfom')} {selectedFaq.username} {translation('onReply')}
                        </label>
                      </div>
                    </div>
                  )}

                  <div className="form-group tw-text-center">
                    <button
                      type="submit"
                      disabled={formik.isSubmitting}
                      className="btn btn-primary"
                    >
                      {translation('submitReply')}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </Modal.Body>
        </Modal>
      )}

      <Modal
        show={showAllFaqs}
        onExit={() => {
          setShowAllFaqs(false);
          setFaqs(initialFaqs);
          typeof onCloseModal === 'function' && onCloseModal();
        }}
        onEntered={() => {
          if (outerFaq) {
            const modalBody = document.getElementById('modal_faqs')
            const target = document.getElementById(`faq_${outerFaq.id}`)
            if (target && modalBody) {
              modalBody.scrollTo({top: target.offsetTop - 150})
            }
          }
        }}
        className="modal-styled"
      >
        <Modal.Header>
          <div className="tw-font-semibold tw-font-styleSans tw-text-xl md:tw-text-2xl">
            {title || 'Helpful Questions'}
          </div>
          <FiX
            size={24}
            className="tw-cursor-pointer"
            onClick={() => {
              setShowAllFaqs(false);
              typeof onCloseModal === 'function' && onCloseModal();
            }}
          />
        </Modal.Header>
        <Modal.Body id="modal_faqs" className="tw-pt-0">
          <div style={{ minHeight: '600px' }}>
            <div className="tw-bg-brand-yellow tw-sticky tw-top-0">
              <div className="input-group tw-pb-5 tw-pt-8">
                <input
                  type="text"
                  ref={inputRef}
                  className="form-control tw-border-r-0 tw-rounded-l-2xl"
                  placeholder={translation('findYourQuestion')}
                  onKeyUp={searchFaqs}
                  onKeyPress={searchFaqs}
                />

                <div className="input-group-append">
                  <div className="input-group-text tw-border-l-0 tw-rounded-tr-2xl tw-rounded-br-2xl tw-cursor-pointer">
                    {query ? (
                      <div
                        className="tw-rounded-full tw-border-solid tw-border-grey-lighter tw-border tw-p-1.5 tw-flex tw-items-center tw-justify-between tw--mr-1"
                        onClick={() => {
                          setQuery(null);
                          filter(null);

                          if (inputRef.current) {
                            inputRef.current.value = '';
                          }
                        }}
                      >
                        <FiX size={16} strokeWidth={2} className="tw-h-4 tw-w-4" />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>

            {faqs.length > 0 ? (
              groupedFaqs.map((group, index) => (
                <div
                  key={group.groupId}
                  className={classNames({
                    'md:tw-pb-5 tw-pb-3': groupedFaqs.length > index + 1,
                  })}
                >
                  {group.groupTitle && (
                    <div
                      className={classNames(
                        'tw-border-b tw-pb-5 tw-pt-3 tw-border-grey-lighter tw-border-solid',
                        {
                          'md:tw-py-5': index > 0,
                        }
                      )}
                    >
                      <div className="tw-font-semibold tw-font-styleSans tw-text-xl tw-text-orange tw-leading-none md:tw-leading-none">
                        {group.groupTitle}
                      </div>
                    </div>
                  )}
                  <div>
                    {sortBy(group.faqs, 'id').map((faq, index) => (
                      <div id={`faq_${faq.id}`} key={faq.id}>
                        <UserFaqItem
                          faq={faq}
                          onClickEdit={(faq) => {
                            setSelectedFaq(faq);
                            setShowUpdateQuestionModal(true);
                          }}
                          onDelete={(faq) => {
                            setFaqs((prev) => prev.filter((o) => o.id !== faq.id));
                          }}
                          showAnwser={outerFaq?.id === faq.id}
                          faqable={faqable}
                          isAdmin={isAdmin}
                          containerClass={index + 1 === group.faqs.length ? 'tw-py-5' : ''}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))
            ) : query ? (
              <div className="tw-text-brand-navy tw-mb-4 tw-leading-none">
                {translation('noResults')}
              </div>
            ) : null}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default UserFaqSection;
